import { isPlatformBrowser } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    Output,
    PLATFORM_ID,
    ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
    faArrowDown,
    faArrowLeft,
    faArrowUp,
    faSearch,
    IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import { MenuItem, MessageService, TreeNode } from "primeng/api";
import { FileUpload } from "primeng/fileupload";
import { forkJoin, Observable, Subscription } from "rxjs";
import { Globals } from "../../app.globals";
import {
    AsyncModalService,
    ButtonPresets,
} from "../../services/shared/async-modal-service";
import { ProcessActivity, UserService } from "../../services/user.service";
import {
    Breadcrumb,
    CreateUrlRequest,
    DuplicateFile,
    File,
    Folder,
    Modal,
    MoveRequest,
    NodeData,
    RenameFileRequest,
    RenameModal,
    RevisionModal,
    Standard,
} from "./dms.models";
import { DmsService } from "./dms.service";

declare var $: any;

@Component({
    standalone: false,
    selector: "dms",
    templateUrl: "./dms.component.html",
    providers: [DmsService],
})
export class DocumentManagementSystem {
    @Input() path: string = "";
    @Input() fileType: string = "File";
    @Input() selectionMode: string = "single";
    @Input() uploadFilesAllowed: boolean = false;
    @Input() allowContextMenu: boolean;
    @Input() skipProjectId: boolean;
    @Input() isFileInput: boolean = false;
    @Input("selection") selectedFile: TreeNode | TreeNode[] = null;
    @Output() selectionChange = new EventEmitter();
    @ViewChild("copyMoveModalButton") modalButton: ElementRef;
    @ViewChild("copyMoveModalCloseButton") modalCloseButton: ElementRef;
    @ViewChild("fileUploader") fileUploader: FileUpload;
    @ViewChild("mainMenu") mainMenu: ElementRef;
    @ViewChild("subMenu") subMenu: ElementRef;
    @ViewChild("renameFolderModalButton") renameFolderModalButton: ElementRef;
    @ViewChild("renameModalInput") renameModalInput: ElementRef;
    @ViewChild("renameButton") renameButton: ElementRef;
    @ViewChild("revisionModalButton") revisionModalButton: ElementRef;
    @ViewChild("multiSelectDropdown") multiSelectDropdown: ElementRef;
    standard: Standard = new Standard();
    modal: Modal = new Modal();
    uploadFolderDisabled: boolean = false;
    dragAndDropDestination: string = "";
    showDragAndDropOverlay: boolean = false;
    isMultiSelect: boolean = false;
    renameModal: RenameModal = new RenameModal(null, true);
    revisionModal: RevisionModal = new RevisionModal(null);
    private lastOrderBy: string = null;
    private sortAscending: boolean = true;
    private folderMenuItems: MenuItem[];
    private fileMenuItems: MenuItem[];
    private multiSelectMenuItems: MenuItem[];
    private baseFolder: TreeNode;
    private searchSubscription: Subscription;
    private uploads: any[] = [];
    private asyncModalService: AsyncModalService;
    hideableColumns = [
        { label: "Modified By", value: "modifiedBy" },
        { label: "Date Modified", value: "modifiedDate" },
        { label: "PIMeo rev", value: "revisionNumber" },
    ];
    selectedColumns = ["modifiedBy", "modifiedDate", "revisionNumber"];
    isViewUser: boolean = false;
    globals: Globals;
    faSearch = faSearch;
    faLeftArrow = faArrowLeft;
    selectAll: boolean = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        private dmsService: DmsService,
        private route: ActivatedRoute,
        private messageService: MessageService,
        private changeDetectorRef: ChangeDetectorRef,
        private userService: UserService,
        globals: Globals,
        asyncModalService: AsyncModalService
    ) {
        this.globals = globals;
        this.asyncModalService = asyncModalService;
    }

    async ngOnInit() {
        if (this.isBrowser()) {
            if (this.selectionMode !== "single") {
                this.standard.simpleView = true;
                this.selectedFile = [];
                this.isMultiSelect = true;
            }

            if (this.uploadFilesAllowed) {
                this.standard.uploadFilesAllowed = true;
            }

            this.dmsService.projectId = this.skipProjectId
                ? 0
                : this.route.snapshot.params.projectId;
            let section = this.route.snapshot.data.type;
            var result = await this.userService
                .canAccessProject(section, this.dmsService.projectId)
                .toPromise();

            if (!result && section == "general") {
                result = { activity: "View", process: null };
            }

            if (result.activity == "View") this.isViewUser = true;

            if (this.path !== undefined) {
                this.baseFolder = this.buildFolderNode(
                    this.path,
                    null,
                    null,
                    false,
                    0,
                    null
                );
                this.baseFolder.expanded = true;

                this.setupFolderMenuItems();
                this.setupFileMenuItems();
                this.setupMultiSelectMenuItems();

                this.refreshFiles(null, true);
            }

            if (this.fileType === "Image") this.standard.accept = "image/*";
        }
    }

    ngAfterViewInit() {
        var modal = $("#copy-move");
        if (modal)
            modal.on("hide.bs.modal", () => {
                this.modal.filter = null;
            });
    }

    onDragOver(evt: DragEvent, node: TreeNode) {
        evt.preventDefault();
        evt.stopPropagation();

        let target: Element = <Element>evt.currentTarget;
        let className: string = "";

        this.dragAndDropDestination = this.getCurrentDirectory();

        if (target.nodeName !== "P-TREE" && node.type == "Folder") {
            target = target.parentElement.parentElement;
            this.dragAndDropDestination = (<any>target).innerText;
        }

        if (this.dragAndDropDestination.indexOf("/") > -1)
            this.dragAndDropDestination = this.dragAndDropDestination.substr(
                this.dragAndDropDestination.lastIndexOf("/") + 1
            );

        className = target.className;

        if (className.indexOf("drag-over") === -1) {
            className += " drag-over";
            className = className.trim();
        }

        target.className = className;
        this.showDragAndDropOverlay = true;
    }

    onDragLeave(evt) {
        evt.preventDefault();
        evt.stopPropagation();

        let target = <Element>evt.currentTarget;
        let className: string = "";

        if (target.nodeName !== "P-TREE")
            target = target.parentElement.parentElement;

        className = target.className;

        className = className.replace(/drag-over/g, "").trim();

        target.className = className;

        this.showDragAndDropOverlay = false;
    }

    async droppedFile(evt, node: TreeNode) {
        evt.preventDefault();
        evt.stopPropagation();

        this.onDragLeave(evt);

        this.uploads = [];

        var nodePath = this.uploadPath();

        if (node && node.type === "Folder")
            nodePath = this.dmsService.getUploadUrl(
                (node.data as NodeData).getPath()
            );

        var items = evt.dataTransfer.items;
        var fileFolders = [];
        let droppedFiles = [];

        for (var i = 0; i < items.length; i++) {
            var item = items[i];
            var fileFolder = item.webkitGetAsEntry();

            if (item.kind == "file") {
                droppedFiles.push({ name: fileFolder.name });
            }

            fileFolders.push(fileFolder);
        }

        let dropped = { files: droppedFiles };

        let duplicates = await this.onSelect(dropped);

        for (var i = 0; i < fileFolders.length; i++) {
            var item = fileFolders[i];
            if (item) {
                let duplicateFile = duplicates.find(
                    (value) => value.existingName == item.name
                );
                let newFileName = null,
                    replacementUrl = null;

                if (duplicateFile) {
                    newFileName = duplicateFile.newFile.newFileName;
                    replacementUrl = duplicateFile.newFile.replaceUrl;
                }

                this.traverseFileTree(
                    item,
                    nodePath,
                    newFileName,
                    replacementUrl
                );
            }
        }
    }

    traverseFileTree(
        item,
        nodePath: string,
        newFileName: string = null,
        replacementUrl: string = null
    ) {
        nodePath = nodePath || "";
        if (item.isFile && !item.name.endsWith(".ignore")) {
            let uploadPath = nodePath.substr(
                0,
                nodePath.indexOf("?folder=") + "?folder=".length
            );
            let queryString = this.dmsService.encodeUriQuery(
                nodePath.substr(
                    nodePath.indexOf("?folder=") + "?folder=".length
                )
            );

            this.standard.showLoading = true;
            this.uploads.push({
                file: newFileName || item.name,
                path: uploadPath + queryString,
                progress: 0,
            });

            let headers = {};

            item.file((file) => {
                var formData = new FormData();

                formData.append("file", file, newFileName || file.name);

                headers["x-file-0-url"] =
                    file.replaceUrl || replacementUrl || "";

                this.dmsService
                    .uploadFileInFolderDropped(
                        uploadPath + queryString,
                        formData,
                        headers
                    )
                    .subscribe((result: File[]) => {
                        result.forEach((file: File) => {
                            var uploadedFile = this.uploads.find((x) => {
                                var tempPath = "";

                                for (var i = 2; i < file.path.length; i++) {
                                    tempPath += file.path[i] + "/";
                                }

                                tempPath = tempPath.substr(
                                    0,
                                    tempPath.length - 1
                                );

                                return (
                                    x.file == file.unalteredFileName &&
                                    x.path.endsWith(
                                        this.dmsService.encodeUriQuery(tempPath)
                                    )
                                );
                            });

                            uploadedFile.progress = 100;

                            var allUploadsCompleted = true;

                            this.uploads.forEach((upload) => {
                                if (upload.progress !== 100)
                                    allUploadsCompleted = false;
                            });

                            if (allUploadsCompleted) this.uploadComplete();
                        });
                    });
            });

            this.detectChanges();
        } else if (item.isDirectory) {
            var dirReader = item.createReader();

            let s3Path = nodePath.substr(
                nodePath.indexOf("?folder=") + "?folder=".length
            );

            this.dmsService
                .createDirectory(s3Path + "/" + item.name, false)
                .subscribe(() => this.detectChanges());

            this.readAllEntries(dirReader, nodePath, item)
                .then((allEntries: any[]) => {
                    for (var i = 0; i < allEntries.length; i++) {
                        this.traverseFileTree(
                            allEntries[i],
                            nodePath + "/" + item.name
                        );
                    }
                })
                .catch(this.errorHandler);        
        }
    }

    private readAllEntries(dirReader: any, nodePath: string, item: any, allEntries: any[] = []): Promise<any[]> {
        return new Promise<any[]>((resolve, reject) => {
            dirReader.readEntries(
                (entries: any[]) => {
                    if (entries.length > 0) {
                        //store retrieved entries
                        allEntries = allEntries.concat(entries);
                        //continue reading entries if there are more
                        this.readAllEntries(dirReader, nodePath, item, allEntries).then(resolve).catch(reject);
                    }
                    else {
                        resolve(allEntries);
                    }
                },
                (error: any) => {
                    reject(error);
                }
            );
        });
    }

    private errorHandler(error: any) {
        this.asyncModalService.getModalResponse("Failed to read all the files");
    }

    isBrowser() {
        return isPlatformBrowser(this.platformId);
    }

    refreshFiles(
        folder: TreeNode,
        usePath?: boolean,
        customPath?: string,
        isModal?: boolean,
        isMove?: boolean
    ) {
        this.selectAll = false;

        if (
            (this.modal.isCopy == false && isMove) ||
            (this.modal.isCopy == true && isMove)
        ) {
            var relativePath = this.standard
                .getCurrentFolderPath()
                .substr(this.path.length);
            this.standard.selectedFolder = this.buildFolderNode(
                this.standard.getCurrentFolderPath(),
                relativePath,
                null,
                false,
                null,
                null
            );
        }

        if (!folder && !customPath) {
            usePath = true;
            customPath = "";
        }

        if (usePath && customPath) {
            this.baseFolder = null;
            this.baseFolder = this.buildFolderNode(
                customPath,
                null,
                null,
                false,
                0,
                null
            );
            this.baseFolder.expanded = true;
        }

        if (!isModal)
            this.standard.currentFolder = folder ? folder : this.baseFolder;

        let path = usePath
            ? customPath || this.path
            : (folder.data as NodeData).getPath() || this.baseFolder.data.path;

        if (isModal) {
            this.modal.showLoading = true;
            this.modal.folders = [];
            this.modal.isTopLevel = path == this.path;
        } else {
            this.standard.showLoading = true;
            this.standard.files = [];
            this.standard.isTopLevel = path == this.path;
        }

        this.dmsService.getFilesAndSubFolders(path).subscribe(
            (data) => {
                let subfolders = [];
                let files = [];

                this.organizeFolders(data.folders);

                if (isModal) {
                    data.folders.forEach((x, index) => {
                        subfolders.push(
                            this.buildFolderNode(
                                x.folderName,
                                null,
                                usePath ? null : this.modal.selectedFolder,
                                false,
                                index,
                                x
                            )
                        );
                    });
                    data.files.forEach((x, index) => {
                        if (!x.hidden)
                            files.push(
                                this.buildFileNode(
                                    x.fileName,
                                    usePath
                                        ? null
                                        : this.standard.selectedFolder,
                                    x.url,
                                    x.file,
                                    x.date,
                                    index,
                                    x
                                )
                            );
                    });

                    this.organizeNodes(files);

                    this.modal.folders = subfolders.concat(files);
                } else {
                    data.folders.forEach((x, index) => {
                        subfolders.push(
                            this.buildFolderNode(
                                x.folderName,
                                null,
                                usePath ? null : this.standard.selectedFolder,
                                false,
                                index,
                                x
                            )
                        );
                    });
                    data.files.forEach((x, index) => {
                        if (!x.hidden)
                            files.push(
                                this.buildFileNode(
                                    x.fileName,
                                    usePath
                                        ? null
                                        : this.standard.selectedFolder,
                                    x.url,
                                    x.file,
                                    x.date,
                                    index,
                                    x
                                )
                            );
                    });

                    this.organizeNodes(files);
                    this.standard.files = subfolders.concat(files);
                }

                this.setBreadcrumbs(path, isModal);

                this.detectChanges();
            },
            null,
            () => {
                if (isModal) this.modal.showLoading = false;
                else this.standard.showLoading = false;

                this.detectChanges();
            }
        );
    }

    nodeUnselect(event) {
        this.selectAll = false;
        if (this.selectionChange) this.selectionChange.emit(this.selectedFile);
    }

    nodeSelect(event, isRightClick: boolean, isModal: boolean) {
        if (!isRightClick && !isModal) {
            if (
                ((this.selectedFile as TreeNode[]) || []).length ==
                this.standard.files.length
            ) {
                this.selectAll = true;
            } else {
                this.selectAll = false;
            }
        }
        if (!isModal)
            this.standard.contextMenu =
                event.node.type === "Folder"
                    ? this.folderMenuItems
                    : this.fileMenuItems;

        if (this.isMultiSelect && !isModal && !this.isFileInput)
            this.standard.contextMenu = this.multiSelectMenuItems;
        else {
            const editable = event.node.data.editable;
            if (event.node.type === "Folder") {
                if (this.isMultiSelect) {
                    let target = event.originalEvent?.target as HTMLElement;
                    let isCheckboxClick =
                        target?.classList?.contains("ui-chkbox-box") ||
                        target?.classList?.contains("p-checkbox-box") ||
                        !!target?.closest?.(".p-checkbox");

                    if (!isCheckboxClick) {
                        if (isModal) {
                            this.modal.selectedFolder = event.node;
                            this.refreshFiles(event.node, null, null, isModal);
                        } else {
                            this.standard.selectedFolder = event.node;

                            if (
                                !editable &&
                                !isRightClick &&
                                this.checkIf3DotMenu(event)
                            ) {
                                this.standard.filter = "";
                                this.selectedFile = [];
                                this.refreshFiles(
                                    event.node,
                                    true,
                                    event.node.data.path,
                                    isModal
                                );
                            }
                        }
                        let sFile = this.selectedFile as any;

                        if (sFile && sFile.length > 0) {
                            sFile[
                                (sFile as any).length - 1
                            ].isIntermidiateFolder = true;
                        }
                    }
                } else {
                    if (isModal) {
                        this.modal.selectedFolder = event.node;
                        this.refreshFiles(event.node, null, null, isModal);
                    } else {
                        this.standard.selectedFolder = event.node;

                        if (
                            !editable &&
                            !isRightClick &&
                            this.checkIf3DotMenu(event)
                        ) {
                            this.standard.filter = "";
                            this.refreshFiles(
                                event.node,
                                true,
                                event.node.data.path,
                                isModal
                            );
                        }
                    }
                }
            } else {
                if (
                    !this.standard.simpleView &&
                    !editable &&
                    !isRightClick &&
                    this.checkIf3DotMenu(event)
                ) {
                    this.selectedFile = event.node;
                    this.standard.filter = "";

                    let url: string;
                    if (
                        ((this.selectedFile as TreeNode).data as NodeData).isUrl
                    ) {
                        url = ((this.selectedFile as TreeNode).data as NodeData)
                            .fileName;
                        url = url.replace(/ /g, "/");
                        url = url.replace(".url", "");
                        url = "http://" + url;
                    } else {
                        url = this.convertUrl(
                            (this.selectedFile as TreeNode).data.url
                        );
                    }

                    window.open(url);
                }
            }
        }
        if (this.selectionChange) this.selectionChange.emit(this.selectedFile);
        this.detectChanges();
    }

    getCurrentDirectory(): string {
        return this.standard.currentFolder
            ? this.standard.currentFolder.data.getPath()
            : this.baseFolder.data.getPath();
    }

    folderPicked(files: File[]) {
        let count = files.length;
        this.uploadFolderDisabled = true;
        this.standard.files = [];
        this.standard.showLoading = true;

        for (let i = 0; i < files.length; i++) {
            let file = files[i];
            if (file.name.endsWith(".ignore")) {
                count--;
            } else {
                let filePath = file.webkitRelativePath.replace(
                    `/${file.name}`,
                    ""
                );

                let folders = filePath.split("/");
                let dmsPath = this.uploadPath();

                for (var j = 0; j < folders.length; j++) {
                    let s3Path = dmsPath.substr(
                        dmsPath.indexOf("?folder=") + "?folder=".length
                    );
                    let folderPath = folders.slice(0, j + 1).join("/");
                    this.dmsService
                        .createDirectory(`${s3Path}/${folderPath}`, false)
                        .subscribe();
                }

                let fullPath = `${dmsPath}/${filePath}`;
                let formData: FormData = new FormData();
                formData.append("file", <any>file, file.name);

                this.dmsService
                    .uploadFileInFolder(fullPath, formData)
                    .subscribe(
                        () => {
                            count--;
                            this.checkRemainingFilesCount(count);
                        },
                        (err) => {
                            count--;
                            this.asyncModalService.getModalResponse(err.error);
                            this.checkRemainingFilesCount(count);
                        }
                    );
            }
        }
    }

    private checkRemainingFilesCount(count: number) {
        if (count === 0) {
            this.uploadFolderDisabled = false;
            this.refreshFiles(null, true, this.standard.getCurrentFolderPath());
            this.standard.showLoading = false;
        }
    }

    uploadError($event, error) {
        this.dmsService.hideSpinner();
        this.standard.showLoading = false;

        this.showFileError();

        this.refreshFiles(null, true, this.standard.getCurrentFolderPath());
    }

    onBeforeUpload($event) {
        this.standard.files = [];
        this.standard.showLoading = true;
    }

    uploadComplete() {
        this.fileUploader.clear();
        this.standard.showLoading = false;
        this.refreshFiles(null, true, this.standard.getCurrentFolderPath());
        this.detectChanges();
    }

    async onSelect($event) {
        var duplicatesFound: DuplicateFile[] = [];

        for (var i = 0; i < $event.files.length; i++) {
            var currentFile = $event.files[i];
            var existingFiles = this.standard.files.filter((node: TreeNode) => {
                return node.label == currentFile.name;
            });
            if (existingFiles.length > 0)
                duplicatesFound.push({
                    existingName: currentFile.name,
                    duplicates: existingFiles,
                    newFile: currentFile,
                });
        }

        if (duplicatesFound.length > 0) {
            let names = duplicatesFound
                .map((value) => value.existingName)
                .join("</li><li>");
            let createNewFile = await this.asyncModalService.getModalResponse(
                `<p>The following files already exist in this folder:</p><ul><li>${names}</li></ul><p>Would you like to keep the files separate?</p>`,
                ButtonPresets.YesNo
            );
            if (createNewFile) {
                for (var i = 0; i < duplicatesFound.length; i++) {
                    var current = duplicatesFound[i];
                    current.newFile.newFileName = this.getIncrementalFileName(
                        this.standard.files,
                        current.existingName
                    );
                }
            } else {
                for (var i = 0; i < duplicatesFound.length; i++) {
                    var current = duplicatesFound[i];
                    if (current.duplicates.length == 1)
                        current.newFile.replaceUrl = (<File>(
                            current.duplicates[0].data
                        )).url;
                    else
                        this.asyncModalService.getModalResponse(
                            "Too many duplicates found. Please manually edit some file names to improve uniqueness."
                        );
                }
            }
        }

        return duplicatesFound;
    }

    getIncrementalFileName(files: TreeNode[], existingName: string): any {
        var nameToMatch = existingName;
        var extension = "";
        var allNames = files.map((value) => value.label).join("\r\n");

        if (nameToMatch.lastIndexOf(".") > -1) {
            extension = nameToMatch.substr(nameToMatch.lastIndexOf(".") + 1);
            nameToMatch = nameToMatch.substr(0, nameToMatch.lastIndexOf("."));
        }

        nameToMatch = nameToMatch.trim();

        let regex = new RegExp(
            "^" + nameToMatch + "\\s?\\(?(\\d+)?\\)?\\." + extension + "$",
            "gm"
        );
        let matches = null;
        let currentIncrement = 0;

        while ((matches = regex.exec(allNames)) != null) {
            if (matches.index === regex.lastIndex) {
                regex.lastIndex++;
            }
            var newIncrement = +matches[1] || 0;
            if (newIncrement > currentIncrement)
                currentIncrement = newIncrement;
        }

        return `${nameToMatch} (${++currentIncrement}).${extension}`;
    }

    async uploadHandler(event) {
        this.hideMenus();
        await this.onSelect(event);
        this.onBeforeUpload(event);

        var formData = new FormData();
        var headers = {};

        for (var i = 0; i < event.files.length; i++) {
            var file = event.files[i];
            var fileName = file.newFileName || file.name;

            headers["x-file-" + i + "-url"] = file.replaceUrl || "";

            formData.append("file", file, fileName);
        }

        this.standard.showLoading = true;

        this.dmsService
            .uploadFileInFolder(this.uploadPath(), formData, headers)
            .subscribe(
                null,
                (error) => this.uploadError(event, error),
                () => this.uploadComplete()
            );
    }

    getColumnSrc(data) {
        return `/api/thumbnail/300?file=${data.url}`;
    }

    createUrl() {
        let url = this.standard.urlLink;
        url = url.replace("https://", "");
        url = url.replace("http://", "");
        url = url.replace(/\//g, " ");

        const request: CreateUrlRequest = {
            filename: url + ".url",
            url: url,
            path: this.standard.selectedFolder
                ? this.standard.selectedFolder.data.getPath()
                : this.baseFolder.data.getPath(),
        };

        this.dmsService.createUrl(request).subscribe(
            () => {
                this.refreshFiles(
                    null,
                    true,
                    this.standard.getCurrentFolderPath()
                );
                this.standard.urlLink = "";
            },
            () => this.showPermissionError()
        );
    }

    createFolder(
        parentNode: TreeNode,
        folderName: string,
        calledFromMenu: boolean = false
    ) {
        parentNode = parentNode || this.baseFolder;
        let newFolderName = folderName || this.getNewFolderName();

        let existingNode: TreeNode;
        let workingFolders = this.getFoldersInCurrentPath();

        if (calledFromMenu) this.hideMenus();

        if (parentNode) workingFolders = parentNode.children;

        existingNode = this.findNode(workingFolders, newFolderName);

        if (!existingNode) {
            let newFolder = this.buildFolderNode(
                newFolderName,
                null,
                parentNode,
                true,
                this.standard.files.length,
                null
            );
            (newFolder.data as NodeData).originalPath = "";
            this.updateModified(newFolder.data);

            existingNode = newFolder;
            this.standard.files.splice(0, 0, newFolder);

            this.organizeNodes(workingFolders);

            this.standard.selectedFolder = existingNode;
            this.dmsService
                .createDirectory((existingNode.data as NodeData).getPath())
                .subscribe(
                    (data) => {
                        (
                            this.standard.selectedFolder.data as NodeData
                        ).originalPath = data;
                    },
                    () => this.showPermissionError()
                );
            this.showRenameNodeModal(existingNode, true);
        }
    }

    characterCheck(event: any) {
        if (event.key == "/") {
            return false;
        }
    }

    async renameFolder(
        node: TreeNode,
        isModal: boolean,
        newName: string
    ): Promise<boolean> {
        if (isModal) {
            this.copyMoveFileModal();
        } else {
            if (!node.label)
                node.label = this.getFolderFromPath(node.data.originalPath);

            var listOfChildren = this.standard.files;
            var continueToRename = true;
            var duplicate = false;
            if (
                listOfChildren.find(
                    (p) =>
                        p.label == newName &&
                        p.data.originalPath != node.data.originalPath
                ) != null
            ) {
                continueToRename = false;
                var response = await this.asyncModalService.getModalResponse(
                    "Warning: there is already a folder with this name, renaming this folder will result in a merger of the two folders. Are you sure you want to continue?",
                    ButtonPresets.YesNo
                );
                if (response) {
                    continueToRename = true;
                    duplicate = true;
                }
            }

            if (node.label.indexOf("/") > -1) {
                this.asyncModalService.getModalResponse(
                    "Please note that the following characters ('/' ) are not allowed in names, please use an alternate character"
                );
                continueToRename = false;
            }

            let folderName = newName;

            if (continueToRename) {
                const nodeData = node.data as NodeData;
                nodeData.editable = false;

                let newPath = nodeData
                    .getPath()
                    .replace(node.label, folderName);

                if (nodeData.originalPath !== newPath) {
                    this.dmsService
                        .renameFolder({
                            oldPath: nodeData.originalPath,
                            newPath: newPath,
                            isCopy: false,
                            isReplace: false,
                        })
                        .subscribe(
                            null,
                            () => this.showPermissionError(),
                            () => {
                                this.refreshFiles(
                                    null,
                                    true,
                                    this.getCurrentDirectory(),
                                    isModal
                                );
                            }
                        );
                } else
                    this.standard.selectedFolder =
                        this.standard.selectedFolder.data.parent;
            } else {
                this.refreshFiles(
                    null,
                    true,
                    this.getCurrentDirectory(),
                    isModal
                );
                return false;
            }
        }
        return true;
    }

    async renameFile(
        node: TreeNode,
        isModal: boolean,
        newName: string
    ): Promise<boolean> {
        if (isModal) {
            this.copyMoveFileModal();
        } else {
            const nodeData = node.data as NodeData;
            nodeData.editable = false;

            var listOfChildren = this.standard.files;
            var keepFilesSeparate = false;
            const duplicateFile = listOfChildren.find(
                (p) => p.label == newName && p.data.url != node.data.url
            );

            if (duplicateFile) {
                keepFilesSeparate =
                    await this.asyncModalService.getModalResponse(
                        "Warning: there is already a file with this name. Do you want to keep the files separate?",
                        ButtonPresets.YesNo
                    );
            }

            let fileName = newName;

            if (duplicateFile && keepFilesSeparate)
                fileName = this.getNewFileName(fileName, listOfChildren);

            const relativeUrl = nodeData.url.substr(
                nodeData.url.indexOf(this.getCurrentDirectory())
            );
            const oldPath = relativeUrl;
            let newPath = relativeUrl.replace(nodeData.fileName, fileName);

            let isReplace: boolean =
                !keepFilesSeparate && duplicateFile != null;

            if (isReplace)
                newPath = duplicateFile.data.url.substr(
                    duplicateFile.data.url.indexOf(this.getCurrentDirectory())
                );

            if (oldPath === newPath) return;

            this.dmsService
                .renameFile({
                    oldPath: oldPath,
                    newPath: newPath,
                    isCopy: false,
                    newFileName: fileName,
                    isReplace: isReplace,
                })
                .subscribe(
                    () => {
                        this.refreshFiles(
                            null,
                            true,
                            this.getCurrentDirectory(),
                            isModal
                        );
                    },
                    () => this.showPermissionError()
                );
        }

        return true;
    }

    private copyMoveFileModal() {
        var selectedModalFolder = this.modal.selectedFolder || this.baseFolder;
        var selectedfiles: TreeNode[] = Array.isArray(this.selectedFile)
            ? this.selectedFile
            : [this.selectedFile];

        var observables: Observable<any>[] = [];

        selectedfiles.forEach((node: TreeNode) => {
            var nodeData = node.data as NodeData;

            if (node.type !== "Folder") {
                var oldPath = nodeData.url.substr(
                    nodeData.url.indexOf(this.standard.getCurrentFolderPath())
                );
                var newPath = oldPath.replace(
                    oldPath.substr(0, oldPath.lastIndexOf("/")),
                    selectedModalFolder.data.getPath()
                );

                if (oldPath == newPath) return;

                newPath = newPath.replace(
                    nodeData.fileName,
                    nodeData.self.label
                );

                let request: RenameFileRequest = {
                    oldPath: oldPath,
                    newPath: newPath,
                    isCopy: this.modal.isCopy,
                    newFileName: nodeData.self.label,
                    isReplace: false,
                };

                observables.push(this.dmsService.renameFile(request));
            } else {
                var oldPath = nodeData.getPath();
                var newPath =
                    this.modal.getCurrentFolderPath() + "/" + node.label;

                if (oldPath == newPath) return;

                let request: MoveRequest = {
                    isCopy: this.modal.isCopy,
                    oldPath: oldPath,
                    newPath: newPath,
                    isReplace: false,
                };

                observables.push(this.dmsService.renameFolder(request));
            }
        });

        this.modalCloseButton.nativeElement.click();

        if (!this.standard.simpleView) {
            this.isMultiSelect = false;
            this.isMultiSelectChanged(false);
        }

        if (observables.length > 0)
            forkJoin(observables).subscribe(
                () => {
                    if (!this.modal.isCopy)
                        this.refreshFiles(
                            null,
                            true,
                            this.standard.getCurrentFolderPath(),
                            false,
                            true
                        );
                },
                () => this.showPermissionError()
            );
    }

    uploadPath(): string {
        if (this.path === undefined) return null;

        let activeFolder = this.standard.currentFolder || this.baseFolder;

        if (!activeFolder) return "";

        return this.dmsService.getUploadUrl(
            (activeFolder.data as NodeData).getPath()
        );
    }

    search(isModal: boolean, event: Event): void {
        event.preventDefault();
        event.stopPropagation();

        const filter = isModal
            ? (this.modal.filter || "").trim()
            : (this.standard.filter || "").trim();

        if (filter.length == 0) {
            this.asyncModalService.getModalResponse(
                "A value must be entered in order to search."
            );
            return;
        }

        let path;
        if (isModal) {
            this.modal.files = [];
            this.modal.showLoading = true;
            path = this.modal.getCurrentFolderPath();
            this.modal.isSearching = true;
        } else {
            this.standard.files = [];
            this.standard.showLoading = true;
            path = this.standard.getCurrentFolderPath();
            this.standard.isSearching = true;
        }

        this.detectChanges();

        this.searchSubscription = this.dmsService
            .search(path, filter)
            .subscribe(
                (data) => {
                    let subfolders = [];
                    let files = [];

                    this.organizeFolders(data.folders);
                    data.folders.forEach((x, index) => {
                        subfolders.push(
                            this.buildFolderNode(
                                x.folderName,
                                x.relativeFilePath,
                                null,
                                false,
                                index,
                                x
                            )
                        );
                    });

                    if (isModal) {
                        this.modal.folders = subfolders;
                        this.modal.isSearching = false;
                    } else {
                        data.files.forEach((x, index) => {
                            if (!x.hidden)
                                files.push(
                                    this.buildFileNode(
                                        x.fileName,
                                        null,
                                        x.url,
                                        null,
                                        x.date,
                                        index,
                                        x
                                    )
                                );
                        });

                        this.organizeNodes(files);
                        this.standard.files = subfolders.concat(files);
                        this.standard.isSearching = false;
                    }
                },
                () =>
                    isModal
                        ? (this.modal.showLoading = false)
                        : (this.standard.showLoading = false),
                () => {
                    isModal
                        ? (this.modal.showLoading = false)
                        : (this.standard.showLoading = false);

                    this.detectChanges();
                }
            );
    }

    cancel(isModal: boolean) {
        this.checkIsSearching(isModal);

        if (isModal) {
            this.modal.selectedFolder = this.baseFolder;
            this.modal.filter = "";
        } else {
            this.standard.selectedFolder = this.baseFolder;
            this.standard.filter = "";
            this.selectedFile = [];
        }
        this.refreshFiles(null, true, this.path, isModal);
    }

    cancelRename(isNew: boolean) {
        if (isNew) {
            let pathToDelete = (
                this.standard.selectedFolder.data as NodeData
            ).getPath();

            this.dmsService.deleteFolder(pathToDelete).subscribe(
                () => {
                    this.refreshFiles(
                        null,
                        true,
                        this.standard.getCurrentFolderPath()
                    );
                    this.standard.selectedFolder =
                        this.standard.selectedFolder.data.parent;
                },
                () => this.showPermissionError()
            );
        }
    }

    back(isModal: boolean) {
        if (isModal)
            this.modal.selectedFolder = this.modal.selectedFolder.data.parent;
        else
            this.standard.selectedFolder =
                this.standard.selectedFolder.data.parent;

        let selectedFolder: TreeNode;
        let path: string;
        let breadcrumbs: Breadcrumb[];

        if (!isModal) {
            selectedFolder = this.standard.selectedFolder;
            breadcrumbs = this.standard.breadcrumbs;
        } else {
            selectedFolder = this.modal.selectedFolder;
            breadcrumbs = this.modal.breadcrumbs;
        }

        path = breadcrumbs[breadcrumbs.length - 2].path;

        this.selectedFile = [];

        this.refreshFiles(selectedFolder, true, path, isModal);
    }

    getSharableLink(text: string) {
        var newString = encodeURI(text);

        const el = document.createElement("textarea");
        el.value = newString;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        this.messageService.add({
            key: "popup",
            severity: "info",
            summary: "Link Copied",
            detail: "Link copied to clipboard",
        });
    }

    isBaseFolder(): boolean {
        if (this.baseFolder === this.standard.selectedFolder) {
            this.asyncModalService.getModalResponse(
                "Base folder cannot be deleted"
            );
            return true;
        }

        return false;
    }

    setSelectedFolderPath(breadcrumb: Breadcrumb, isModal: boolean) {
        var path = breadcrumb.path || this.path;
        var selectedFolder = this.buildFolderNode(
            path,
            path,
            null,
            false,
            0,
            null
        );

        if (isModal) this.modal.selectedFolder = selectedFolder;
        else {
            this.selectedFile = [];
            this.standard.selectedFolder = selectedFolder;
        }
        this.refreshFiles(null, true, path, isModal);
        if (this.selectionChange) this.selectionChange.emit(this.selectedFile);
        this.detectChanges();
    }

    getFoldersInCurrentPath() {
        return this.standard.files.filter((x) => x.type === "Folder");
    }

    toastConfirm(text: string) {
        this.messageService.clear("confirm");
        this.getSharableLink(text);
    }

    toastReject() {
        this.messageService.clear("confirm");
    }

    downloadFolder(node: TreeNode) {
        const request: CreateUrlRequest = {
            url: (node.data as NodeData).getPath(),
            path: node.label,
            filename: `PIMeo-${node.label}.zip`,
        };

        this.dmsService.downloadFolder(request).subscribe();
    }

    private getFolderOptions(): any[] {
        var newThing = [];
        this.allowContextMenu
            ? (newThing = [
                  {
                      class: "btn",
                      value: "Download",
                      click: () =>
                          this.downloadFolder(this.standard.selectedFolder),
                  },
                  {
                      class: "btn",
                      value: "Add New",
                      click: () =>
                          this.createFolder(this.standard.selectedFolder, null),
                  },
                  {
                      class: "btn",
                      value: "Rename",
                      click: () => {
                          if (!this.isBaseFolder())
                              this.showRenameNodeModal(
                                  this.standard.selectedFolder,
                                  false
                              );
                      },
                  },
                  {
                      class: "btn",
                      value: "Copy",
                      click: (event: MouseEvent, node: TreeNode) => {
                          this.selectedFile = node;
                          this.showCopyMoveModal(true, false);
                          event.preventDefault();
                          event.stopPropagation();
                      },
                  },
                  {
                      class: "btn",
                      value: "Move",
                      click: (event: MouseEvent, node: TreeNode) => {
                          this.selectedFile = node;
                          this.showCopyMoveModal(false, false);
                          event.preventDefault();
                          event.stopPropagation();
                      },
                  },
              ])
            : (newThing = []);

        if (this.allowContextMenu && this.isViewUser == false)
            newThing.push({
                class: "btn",
                value: "Delete",
                click: () => this.deleteFolder(),
            });

        return newThing;
    }

    private setupFolderMenuItems() {
        this.folderMenuItems = this.allowContextMenu
            ? [
                  {
                      label: "Download",
                      icon: "pi pi-download",
                      command: (event) => {
                          this.downloadFolder(this.standard.selectedFolder);
                      },
                  },
                  {
                      label: "Add New",
                      icon: "pi pi-plus",
                      command: (event) => {
                          this.createFolder(this.standard.selectedFolder, null);
                      },
                  },
                  {
                      label: "Rename",
                      icon: "pi pi-pencil",
                      command: () => {
                          if (!this.isBaseFolder())
                              this.showRenameNodeModal(
                                  this.standard.selectedFolder,
                                  false
                              );
                      },
                  },
                  {
                      label: "Copy",
                      icon: "pi pi-copy",
                      command: (event) => this.showCopyMoveModal(true, false),
                  },
                  {
                      label: "Move",
                      icon: "pi pi-file-export",
                      command: (event) => this.showCopyMoveModal(false, false),
                  },
              ]
            : [];

        if (this.allowContextMenu && this.isViewUser == false)
            this.folderMenuItems.push({
                label: "Delete",
                icon: "pi pi-minus",
                command: (event) => this.deleteFolder(),
                visible: this.isViewUser ? false : true,
            });
    }

    private getFileOptions(): any[] {
        var newThing = [];
        this.allowContextMenu
            ? (newThing = [
                  {
                      class: "btn",
                      value: "View",
                      click: () =>
                          window.open(
                              this.convertUrl(
                                  (
                                      (this.selectedFile as TreeNode)
                                          .data as NodeData
                                  ).url
                              )
                          ),
                  },
                  {
                      class: "btn",
                      value: "Rename",
                      click: () =>
                          this.showRenameNodeModal(
                              this.selectedFile as TreeNode,
                              false
                          ),
                  },
                  {
                      class: "btn",
                      value: "Copy",
                      click: (event: MouseEvent, node: TreeNode) => {
                          this.selectedFile = node;
                          this.showCopyMoveModal(true, true);
                          event.preventDefault();
                          event.stopPropagation();
                      },
                  },
                  {
                      class: "btn",
                      value: "Move",
                      click: (event: MouseEvent, node: TreeNode) => {
                          this.selectedFile = node;
                          this.showCopyMoveModal(false, true);
                          event.preventDefault();
                          event.stopPropagation();
                      },
                  },
                  {
                      class: "btn",
                      value: "Sharable Link",
                      click: () =>
                          this.getSharableLink(
                              this.convertUrl(
                                  (
                                      (this.selectedFile as TreeNode)
                                          .data as NodeData
                                  ).url
                              )
                          ),
                  },
                  {
                      class: "btn",
                      value: "Manage Revisions",
                      click: () => {
                          let node = this.selectedFile as TreeNode;
                          let data = node.data as NodeData;
                          this.revisionModal = new RevisionModal(node);
                          this.revisionModal.loading = true;
                          this.revisionModalButton.nativeElement.click();
                          this.dmsService.getVersions(data.url).subscribe(
                              (data) => {
                                  this.revisionModal.versions = data;
                              },
                              null,
                              () => {
                                  this.revisionModal.loading = false;
                              }
                          );
                      },
                  },
              ])
            : (newThing = []);

        if (this.allowContextMenu && this.isViewUser == false)
            newThing.push({
                value: "Delete",
                icon: "pi pi-minus",
                command: (event) => this.deleteFolder(),
                visible: this.isViewUser ? false : true,
            });

        return newThing;
    }

    private setupFileMenuItems() {
        this.fileMenuItems = this.allowContextMenu
            ? [
                  {
                      label: "View",
                      icon: "pi pi-eye",
                      command: () =>
                          window.open(
                              this.convertUrl(
                                  (
                                      (this.selectedFile as TreeNode)
                                          .data as NodeData
                                  ).url
                              )
                          ),
                  },
                  {
                      label: "Rename",
                      icon: "pi pi-pencil",
                      command: () =>
                          this.showRenameNodeModal(
                              this.selectedFile as TreeNode,
                              false
                          ),
                  },
                  {
                      label: "Copy",
                      icon: "pi pi-copy",
                      command: () => this.showCopyMoveModal(true, true),
                  },
                  {
                      label: "Move",
                      icon: "pi pi-file-export",
                      command: () => this.showCopyMoveModal(false, true),
                  },
                  {
                      label: "Sharable Link",
                      icon: "pi pi-share-alt",
                      command: () =>
                          this.getSharableLink(
                              this.convertUrl(
                                  (
                                      (this.selectedFile as TreeNode)
                                          .data as NodeData
                                  ).url
                              )
                          ),
                  },
                  {
                      label: "Manage Revisions",
                      icon: "pi pi-clock",
                      command: () => {
                          let node = this.selectedFile as TreeNode;
                          let data = node.data as NodeData;
                          this.revisionModal = new RevisionModal(node);
                          this.revisionModal.loading = true;
                          this.revisionModalButton.nativeElement.click();
                          this.dmsService.getVersions(data.url).subscribe(
                              (data) => {
                                  this.revisionModal.versions = data;
                              },
                              null,
                              () => {
                                  this.revisionModal.loading = false;
                              }
                          );
                      },
                  },
              ]
            : [];

        if (this.allowContextMenu && this.isViewUser == false)
            this.fileMenuItems.push({
                label: "Delete",
                icon: "pi pi-minus",
                command: () => this.deleteFiles(),
                visible: this.isViewUser ? false : true,
            });
    }

    private setupMultiSelectMenuItems() {
        this.multiSelectMenuItems = this.allowContextMenu
            ? [
                  {
                      label: "Copy",
                      icon: "pi pi-copy",
                      command: () => this.showCopyMoveModal(true, true),
                  },
                  {
                      label: "Move",
                      icon: "pi pi-file-export",
                      command: () => this.showCopyMoveModal(false, true),
                  },
                  {
                      label: "Delete",
                      icon: "pi pi-minus",
                      command: () => this.deleteFiles(),
                  },
              ]
            : [];
    }

    private async deleteFolder() {
        if (!this.isBaseFolder()) {
            var response = await this.asyncModalService.getModalResponse(
                "Are you sure you want to delete this folder and all of its contents?",
                ButtonPresets.YesNo
            );
            if (response) {
                let pathToDelete = (
                    this.standard.selectedFolder.data as NodeData
                ).getPath();
                this.dmsService.deleteFolder(pathToDelete).subscribe(
                    () => {
                        this.refreshFiles(
                            null,
                            true,
                            this.standard.getParentFolderPath()
                        );
                        this.standard.selectedFolder =
                            this.standard.selectedFolder.data.parent;
                    },
                    () => this.showPermissionError()
                );
            }
        }
    }

    private async deleteFiles() {
        this.hideMenus();
        var selectedFiles: TreeNode[] = Array.isArray(this.selectedFile)
            ? this.selectedFile
            : [this.selectedFile];
        var confirmMessage =
            selectedFiles.length <= 1
                ? "Are you sure you want to delete this file?"
                : "Are you sure you want to delete these files/folders?";

        var response = await this.asyncModalService.getModalResponse(
            confirmMessage,
            ButtonPresets.YesNo
        );
        if (response) {
            var observables: Observable<any>[] = [];

            (selectedFiles as TreeNode[]).forEach((node: TreeNode) => {
                if (node.type !== "Folder")
                    observables.push(
                        this.dmsService.deleteFile((node.data as NodeData).url)
                    );
                else
                    observables.push(
                        this.dmsService.deleteFolder(
                            (node.data as NodeData).getPath()
                        )
                    );
            });

            forkJoin(observables).subscribe(
                () => {
                    if (!this.standard.simpleView) {
                        this.isMultiSelect = false;
                        this.isMultiSelectChanged(false);
                    }
                    this.refreshFiles(
                        null,
                        true,
                        this.standard.getCurrentFolderPath()
                    );
                },
                () => this.showPermissionError()
            );
        }
    }

    private checkIsSearching(isModal: boolean) {
        const isSearching = isModal
            ? this.modal.isSearching
            : this.standard.isSearching;
        if (isSearching) {
            this.searchSubscription.unsubscribe();
            if (isModal) {
                this.modal.showLoading = false;
                this.modal.isSearching = false;
            } else {
                this.standard.showLoading = false;
                this.standard.isSearching = false;
            }
        }
    }

    private showCopyMoveModal(isCopy: boolean, isFile: boolean) {
        this.hideMenus();
        this.modal.isCopy = isCopy;
        this.modal.isFile = isFile;
        this.modal.selectedFolder = isFile
            ? this.standard.selectedFolder
            : (this.standard.selectedFolder.data as NodeData).parent;
        let path =
            this.standard.breadcrumbs[this.standard.breadcrumbs.length - 1]
                .path;
        this.refreshFiles(
            this.standard.isTopLevel
                ? this.baseFolder
                : this.modal.selectedFolder,
            true,
            path,
            true
        );
        this.modalButton.nativeElement.click();
    }

    private showPermissionError() {
        this.asyncModalService.getModalResponse(
            "You do not have permission to perform this action, please contact your administrator if you require permission.\nAny modifications you make will not be saved"
        );
    }

    private showFileError() {
        this.asyncModalService.getModalResponse(
            "There was an error uploading the file(s).  Please try again"
        );
    }

    private checkIf3DotMenu(event: any): boolean {
        var nodeName = event.originalEvent.target.nodeName;
        var className = event.originalEvent.target.className;

        return (
            (nodeName == "SPAN" && className.includes("pi")) ||
            (nodeName !== "BUTTON" &&
                className !== "btn btn-link" &&
                nodeName !== "SPAN" &&
                className !== "dot")
        );
    }

    private buildFileNode(
        fileName: string,
        parentNode: TreeNode,
        url: string,
        file: any,
        date: Date = null,
        index: number,
        rawData: File
    ): TreeNode {
        const newNode: TreeNode = {};
        newNode.label = fileName;
        newNode.key = url || fileName;
        newNode.icon = this.fileType == "File" ? "pi pi-file" : "pi pi-image";
        newNode.type = this.fileType;
        newNode.children = null;
        newNode.parent = parentNode;

        let data = new NodeData(newNode.label);
        data.editable = false;
        data.url = url;
        data.fileName = fileName;
        data.parent = parentNode;
        data.self = newNode;
        data.date = date;
        data.modifiedDate = rawData.modifiedDate || date;
        data.options = this.getFileOptions();
        data.id = index;
        data.file = file;
        data.addedBy = rawData.addedBy;
        data.modifiedBy = rawData.modifiedBy || rawData.addedBy;
        data.revisionNumber = rawData.revisionNumber;

        if (data.fileName.includes(".url")) data.isUrl = true;

        newNode.data = data;
        return newNode;
    }

    private buildFolderNode(
        name: string,
        relativePath: string,
        parent: TreeNode,
        editable: boolean,
        index: number,
        rawData: Folder
    ): TreeNode {
        let path: string = relativePath || name;
        parent = parent || this.baseFolder;

        if (parent) path = parent.data.getPath() + "/" + name;

        let result: TreeNode = {
            label: name,
            key: path,
            expandedIcon: "pi pi-folder-open",
            collapsedIcon: "pi pi-folder",
            type: "Folder",
            children: [],
            parent: parent,
        };

        let data = new NodeData(path);
        data.path = relativePath || path;
        data.editable = editable;
        data.parent = <TreeNode>parent;
        if (rawData) {
            data.addedBy = rawData.addedBy;
            data.modifiedBy = rawData.modifiedBy || rawData.addedBy;
            data.date = rawData.modifiedDate || rawData.createdDate;
            data.modifiedDate = rawData.modifiedDate || rawData.createdDate;
        } else if (this.userService.user) {
            data.addedBy = this.userService.user.fullName;
            data.modifiedBy = this.userService.user.fullName;
            data.date = new Date();
        }
        result.data = data;
        (result.data as NodeData).self = result;
        data.options = this.getFolderOptions();
        data.id = index;

        return result;
    }

    private setBreadcrumbs(path: string, isModal: boolean): void {
        let pathArray: any[] = path
            .substr(this.path.length)
            .split("/")
            .filter((x) => x);

        let pathLabel = this.path;

        if (pathLabel.indexOf("/") > -1) {
            var index = pathLabel.lastIndexOf("/") + 1;
            pathLabel = pathLabel.substr(index, pathLabel.length - index);
        }

        pathArray.unshift({
            name: pathLabel,
            path: this.path,
        });

        if (isModal)
            this.modal.breadcrumbs =
                pathArray.length > 4
                    ? this.setLongBreadcrumbs(pathArray)
                    : this.setShortBreadcrumbs(pathArray);
        else
            this.standard.breadcrumbs =
                pathArray.length > 4
                    ? this.setLongBreadcrumbs(pathArray)
                    : this.setShortBreadcrumbs(pathArray);
    }

    private setShortBreadcrumbs(pathArray: any[]): Breadcrumb[] {
        let breadcrumbs: Breadcrumb[] = [];

        pathArray.forEach((x, index) => {
            let temp = [...pathArray];
            let tempName = decodeURIComponent(x.name ?? x);
            breadcrumbs.push({
                name: tempName,
                path: temp
                    .slice(0, index + 1)
                    .map((y) => y.path ?? y)
                    .join("/"),
            });
        });

        return breadcrumbs;
    }

    private setLongBreadcrumbs(pathArray: any[]): Breadcrumb[] {
        let breadcrumbs: Breadcrumb[] = [];

        let insertEllipsis = true;

        for (let i = 0; i < pathArray.length; i++) {
            let temp = [...pathArray];

            if (i === 0 || i >= pathArray.length - 3) {
                let tempName = decodeURIComponent(temp[i].name ?? temp[i]);
                breadcrumbs.push({
                    name: tempName,
                    path: temp
                        .slice(0, i + 1)
                        .map((y) => y.path ?? y)
                        .join("/"),
                });
            } else if (insertEllipsis) {
                breadcrumbs.push({
                    name: "...",
                    path: null,
                });
                insertEllipsis = false;
            }
        }

        return breadcrumbs;
    }

    private organizeNodes(nodes: TreeNode[]) {
        if (nodes) {
            this.lastOrderBy = null;
            this.sortBy("label", nodes);
        }
    }

    private organizeFolders(folders: Folder[]) {
        if (folders) {
            folders.sort((a: Folder, b: Folder) => {
                const nameA = a.folderName.toUpperCase();
                const nameB = b.folderName.toUpperCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            });
        }
    }

    private findNode(nodes: TreeNode[], label: string): TreeNode {
        return nodes.find(
            (node) => node.label.toLowerCase() === label.toLowerCase()
        );
    }

    private getFolderFromPath(path: string): string {
        let trim = "/";

        path = path.replace(new RegExp("^[" + trim + "]+"), "");

        let lastIndex = path.lastIndexOf("/");

        return path.substr(lastIndex + 1);
    }

    private getNewFileName(fileName: string, nodes: TreeNode[]): string {
        let iteration = 0;
        let exists = null;
        let originalName: string = fileName;
        let tempFileName: string = fileName;
        let extension: string = "";
        let periodIndex = fileName.lastIndexOf(".");

        if (periodIndex > -1) {
            extension = fileName.substr(periodIndex);
            originalName = tempFileName = fileName.substr(0, periodIndex);
        }

        do {
            tempFileName =
                iteration == 0
                    ? originalName
                    : originalName + " (" + iteration + ")";

            exists = this.findNode(nodes, tempFileName + extension);

            iteration++;
        } while (exists);

        return tempFileName + extension;
    }

    private getNewFolderName(): string {
        let iteration = 0;
        let exists = null;
        let folderName: string;
        let nodes = this.getFoldersInCurrentPath();

        do {
            folderName =
                iteration == 0
                    ? "New Folder"
                    : "New Folder (" + iteration + ")";

            exists = this.findNode(nodes, folderName);

            iteration++;
        } while (exists);

        return folderName;
    }

    private updateModified(node: NodeData): void {
        node.modifiedDate = new Date();
        node.modifiedBy = this.userService.user.fullName;
    }

    sortBy(field: string, treeNodes: TreeNode[]): void {
        if (this.lastOrderBy == field) this.sortAscending = !this.sortAscending;
        else this.sortAscending = true;

        var collator = new Intl.Collator(undefined, {
            numeric: true,
            sensitivity: "base",
        });

        treeNodes.sort((a: TreeNode, b: TreeNode) => {
            let left = "" + (a[field] || a.data[field] || "");
            let right = "" + (b[field] || b.data[field] || "");

            return this.sortAscending
                ? collator.compare(left, right)
                : collator.compare(right, left);
        });

        treeNodes.sort((a: TreeNode, b: TreeNode) => {
            if (a.type === "Folder" && b.type !== "Folder") return -1;
            if (a.type !== "Folder" && b.type === "Folder") return 1;
            return 0;
        });

        this.lastOrderBy = field;
    }

    showSortArrow(field: string): boolean {
        return field == this.lastOrderBy;
    }

    getSortArrowClass(): IconDefinition {
        return this.sortAscending ? faArrowDown : faArrowUp;
    }

    isMultiSelectChanged(isMultiSelect: boolean): void {
        this.selectedFile = [];
        this.selectionMode = isMultiSelect ? "checkbox" : "single";
    }

    toggleMultiSelectDropdown(event: MouseEvent) {
        (
            this.multiSelectDropdown.nativeElement as HTMLDivElement
        ).classList.toggle("show");
        event.stopPropagation();
        event.preventDefault();
    }

    private hideMenu(nativeElement: HTMLDivElement) {
        nativeElement.classList.remove("show");
    }

    hideMenus() {
        this.hideMenu(this.mainMenu.nativeElement);
        this.hideMenu(this.subMenu.nativeElement);
    }

    toggleSelectAll(event: MouseEvent): void {
        this.selectAll = !this.selectAll;

        if (
            ((this.selectedFile as TreeNode[]) || []).length ==
            this.standard.files.length
        )
            this.selectedFile = [];
        else {
            this.selectedFile = [];
            this.standard.files.forEach((x) =>
                (this.selectedFile as TreeNode[]).push(x)
            );
        }
        if (this.selectionChange) this.selectionChange.emit(this.selectedFile);
        this.detectChanges();
    }

    showRenameNodeModal(node: TreeNode, isNew: boolean = false): void {
        (node.data as NodeData).editable = false;
        this.renameModal = new RenameModal(node, isNew);
        this.renameFolderModalButton.nativeElement.click();
        setTimeout(() => {
            this.renameModalInput.nativeElement.focus();
            if (isNew) this.renameModalInput.nativeElement.select();
        }, 500);
    }

    async renameOrCreate(event: MouseEvent, model: RenameModal): Promise<void> {
        model.node.label = model.currentName;
        (model.node.data as NodeData).resetPath();
        let success = false;
        if (model.node.type == "Folder")
            success = await this.renameFolder(
                model.node,
                false,
                model.currentName
            );
        else
            success = await this.renameFile(
                model.node,
                false,
                model.currentName
            );

        if (!success) {
            event.preventDefault();
            event.stopPropagation();
        }
    }

    callRenameButton(): void {
        this.renameButton.nativeElement.click();
    }

    private detectChanges(): void {
        if (!this.changeDetectorRef["destroyed"])
            this.changeDetectorRef.detectChanges();
    }

    getSize(size: number): number {
        let byte = 1,
            kb = byte * 1024,
            mb = kb * 1024,
            gb = mb * 1024,
            tb = gb * 1024;

        if (size >= tb) return size / tb;
        else if (size >= gb) return size / gb;
        else if (size >= mb) return size / mb;
        else if (size >= kb) return size / kb;
        else return size;
    }

    getSizeMetric(size: number): string {
        let byte = 1,
            kb = byte * 1024,
            mb = kb * 1024,
            gb = mb * 1024,
            tb = gb * 1024;

        if (size >= tb) return "TB";
        else if (size >= gb) return "GB";
        else if (size >= mb) return "MB";
        else if (size >= kb) return "KB";
        else return "B";
    }

    downloadFileVersion(
        url: string,
        versionId: string,
        dropdownMenuId: string
    ): void {
        window.open(this.convertUrl(url) + "?versionId=" + versionId);

        this.hideMenu(
            document.getElementById(dropdownMenuId) as HTMLDivElement
        );
    }

    showColumn(field: string) {
        return this.selectedColumns.indexOf(field) > -1;
    }

    getLabelClass() {
        var width = 12;

        if (this.selectedColumns.indexOf("revisionNumber") > -1) width--;

        for (var widerColumn of this.selectedColumns.filter(
            (x) => x != "revisionNumber"
        ))
            width -= 2;

        return "col-" + width;
    }

    getNameColumnWidth() {
        if (window.innerWidth <= 550)
            return {
                "max-width": `calc(100% - ${
                    this.globals.scrollbarWidth + 30
                }px)`,
                flex: `0 0 calc(100% - ${this.globals.scrollbarWidth + 30}px)`,
            };

        var width = 12;

        if (this.selectedColumns.indexOf("revisionNumber") > -1) width--;

        for (var widerColumn of this.selectedColumns.filter(
            (x) => x != "revisionNumber"
        ))
            width -= 2;

        let valueToUse = this.globals.scrollbarWidth;

        if (this.globals.scrollbarWidth == 0) {
            valueToUse = 17;
        }

        return {
            "max-width": `calc((100% / 12 * ${width}) - ${valueToUse + 30}px)`,
            flex: `0 0 calc((100% / 12 * ${width}) - ${valueToUse + 30}px)`,
        };
    }

    getBlankColumnWidth() {
        let valueToUse = this.globals.scrollbarWidth;
        if (this.globals.scrollbarWidth == 0) {
            valueToUse = 17;
        }

        return {
            "max-width": `${valueToUse + 30}px`,
            flex: `0 0 ${valueToUse + 30}px`,
        };
    }

    getClassesForMainTree() {
        return {
            "multiple-selection-mode": this.selectionMode == "checkbox",
            "single-selection-mode": this.selectionMode == "single",
            "file-type-image": this.fileType == "Image",
            "no-records":
                this.standard.files.length == 0 && !this.standard.showLoading,
        };
    }

    private convertUrl(url: string): string {
        return url
            .replace("%", "%25")
            .replace("+", "%2B")
            .replace("#", "%23")
            .replace("?", "%3F");
    }

    openCm(event, fileContextMenu, node) {
        event.preventDefault();
        event.stopPropagation();
        if (node.type === "Folder") {
            this.standard.contextMenu = this.folderMenuItems;
            this.standard.selectedFolder = node;
        } else {
            this.standard.contextMenu = this.fileMenuItems;
            this.selectedFile = node;
        }
        fileContextMenu.show(event);
        if (this.selectionChange) this.selectionChange.emit(this.selectedFile);
        this.detectChanges();
        return false;
    }
}
